import { supabase } from '@/lib/supabase';

/**
 * Activation d'un thème (ou du design natif) pour un commerce.
 *
 * Deux sources doivent rester cohérentes :
 *  • `sitewebthemeactuelle` : le thème réellement choisi par le commerce
 *    (status = "active" ou "native" quand le design natif est choisi),
 *  • `users.theme` : l'identifiant du thème historique (vide = design natif).
 *
 * Le site public lit d'abord `sitewebthemeactuelle` puis retombe sur
 * `users.theme`. Ces helpers garantissent donc les deux écritures.
 */

export type MerchantDesignMode = 'theme' | 'native';

export interface MerchantDesignState {
  mode: MerchantDesignMode;
  themeId: number | null;
  nomtheme: string | null;
}

export interface ActivatedThemeInput {
  id: number;
  titre?: string | null;
  prix?: string | null;
}

/** Lit l'état courant du design d'un commerce (thème ou natif). */
export async function getMerchantDesignState(commerceId: number): Promise<MerchantDesignState> {
  const { data } = await supabase
    .from('sitewebthemeactuelle')
    .select('id, idtheme, nomtheme, status, datedactivation')
    .eq('idcommerce', commerceId)
    .order('datedactivation', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (data && data.status !== 'native') {
    return { mode: 'theme', themeId: data.idtheme ?? null, nomtheme: data.nomtheme ?? null };
  }
  return { mode: 'native', themeId: null, nomtheme: null };
}

/** Applique un thème à un commerce. */
export async function activateThemeForMerchant(
  commerceId: number,
  theme: ActivatedThemeInput,
): Promise<void> {
  const payload = {
    idtheme: theme.id,
    nomtheme: theme.titre || '',
    prix: theme.prix || '0',
    idcommerce: commerceId,
    datedactivation: new Date().toISOString(),
    status: 'active',
  };

  const { data: existing } = await supabase
    .from('sitewebthemeactuelle')
    .select('id')
    .eq('idcommerce', commerceId)
    .order('datedactivation', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (existing?.id) {
    const { error } = await supabase
      .from('sitewebthemeactuelle')
      .update(payload)
      .eq('id', existing.id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('sitewebthemeactuelle').insert(payload);
    if (error) throw error;
  }

  // Miroir historique attendu par le site public.
  try {
    await supabase.from('users').update({ theme: String(theme.id) }).eq('id', commerceId);
  } catch {
    /* best-effort : le statut "active" suffit au rendu */
  }
}

/** Bascule un commerce sur le design natif Zifek (sans thème). */
export async function activateNativeDesign(commerceId: number): Promise<void> {
  const payload = {
    idtheme: 0,
    nomtheme: 'Design natif Zifek',
    prix: '0',
    idcommerce: commerceId,
    datedactivation: new Date().toISOString(),
    status: 'native',
  };

  const { data: existing } = await supabase
    .from('sitewebthemeactuelle')
    .select('id')
    .eq('idcommerce', commerceId)
    .order('datedactivation', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (existing?.id) {
    const { error } = await supabase
      .from('sitewebthemeactuelle')
      .update(payload)
      .eq('id', existing.id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('sitewebthemeactuelle').insert(payload);
    if (error) throw error;
  }

  // `users.theme` vide = aucun thème imposé → design natif.
  try {
    await supabase.from('users').update({ theme: null }).eq('id', commerceId);
  } catch {
    /* best-effort : le statut "native" suffit au rendu */
  }
}